import React from 'react';
import { Card, CardFooter } from 'reactstrap';
import PropTypes from 'prop-types';

/**
 * BuildAithAiCard Component
 * 
 * A reusable card component for displaying sample prompts and messages.
 * Supports both legacy (handleSelect) and modern (onSelect) callback prop names
 * for seamless integration with existing and new components.
 * 
 * @component
 * @param {Object} props - Component props
 * @param {string} props.title - The title/heading of the card
 * @param {string} props.description - The full description/content of the card
 * @param {Function} [props.handleSelect] - Callback when card is clicked (legacy, use onSelect)
 * @param {Function} [props.onSelect] - Callback when card is clicked (preferred)
 * @param {string} [props.buttonText='Insert as prompt'] - Custom text for the action button
 * 
 * @example
 * // For AI prompts (existing usage)
 * <BuildAithAiCard
 *   title="Employee Exit Survey"
 *   description="Create an exit interview survey..."
 *   handleSelect={() => setPrompt("...")}
 *   buttonText="Insert as prompt"
 * />
 * 
 * // For sample messages (new usage)
 * <BuildAithAiCard
 *   title="Smooth Workflow"
 *   description="Is there anything we can improve..."
 *   onSelect={() => setMessage("...")}
 *   buttonText="Insert Text"
 * />
 */
const BuildAithAiCard = ({ 
  title, 
  description, 
  handleSelect,
  onSelect,
  buttonText = 'Insert as prompt' 
}) => {
  
  // Support both handleSelect (legacy) and onSelect (new) for backward compatibility
  // onSelect takes priority if both are provided
  const handleCardSelect = onSelect || handleSelect;

  /**
   * Truncates text to 140 characters, attempting to break at word boundary
   * @param {string} str - The string to truncate
   * @returns {string} Truncated string with ellipsis if needed
   */
  function truncateString(str) {
    if (str.length > 140) {
      let truncatedStr = str.slice(0, 140);
      let lastSpaceIndex = truncatedStr.lastIndexOf(" ");
      if (lastSpaceIndex !== -1) {
        truncatedStr = truncatedStr.slice(0, lastSpaceIndex);
      }
      return truncatedStr + "...";
    }
    return str;
  }

  return (
    <Card 
      style={{ height: "100%" }} 
      className="sharepage_item_card p-1" 
      onClick={handleCardSelect}
      role="button"
      tabIndex={0}
      onKeyPress={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          handleCardSelect();
        }
      }}
    >
      <div className="text-center p-2">
        <div className="share_page_card_heading">{title}</div>
        <div 
          className="ml-0 mt-4 trivvy_samples_card_desc" 
          style={{ color: "black" }}
        >
          {truncateString(description)}
        </div>
      </div>
      <CardFooter className="text-center insert_prompt_footer p-2">
        <div className="cursor-pointer text-info">
          {buttonText}
        </div>
      </CardFooter>
    </Card>
  );
};

BuildAithAiCard.propTypes = {
  title: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  handleSelect: PropTypes.func,
  onSelect: PropTypes.func,
  buttonText: PropTypes.string,
};

export default BuildAithAiCard;
